import { HOUR } from '../transactions/transaction';
import { parseDateFromISO8601, stringifyDateToISO8601, getStartOf, addTime } from './dates';

/**
 * The marketplace operates entirely within Switzerland, which is a single time zone.
 *
 * Sharetribe's stock search builder uses 'Etc/UTC' and pads the query window by -14h/+12h so
 * that listings in any time zone are covered. That padding widens a one-day query into a
 * ~50-hour window and forces `availability` down to 'time-partial'. Because every listing
 * here is in Zurich, we can query the real zone and ask an exact question instead.
 *
 * If the marketplace ever expands beyond Switzerland, this constant is the one place that
 * has to be reckoned with.
 */
export const MARKETPLACE_TIME_ZONE = 'Europe/Zurich';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Name of the query param that carries month mode, and its only meaningful value.
 *
 * Exported so that every writer and reader spells it the same way; this module never reads the
 * URL itself.
 */
export const DATE_MODE_QUERY_PARAM = 'dateMode';
export const DATE_MODE_MONTH = 'month';

/**
 * @typedef {{ mode: 'day', date: string }
 *   | { mode: 'month', month: string }
 *   | { mode: 'range', start: string, end: string }} DateContext
 */

/**
 * Read the `dates` and `dateMode` query params into a DateContext.
 *
 * `dates` keeps Sharetribe's existing `<from>,<to>` shape so that existing links and the
 * validation in SearchPage.shared.js keep working. Month mode is carried explicitly in
 * `dateMode`, and is never derived:
 *
 *   from === to                                 -> day
 *   dateMode === 'month' AND a whole month span -> month
 *   anything else spanning >1 day               -> range
 *
 * Month mode used to be derived from the range shape alone — a span that happened to be exactly
 * one calendar month became month mode. That derivation is only sound when the `dateSearch`
 * feature gate is on and the day/month picker is the only writer. Off — the default — the stock
 * FieldDateRangeController and the PageBuilder hero SearchCTA can both emit
 * `dates=2027-06-01,2027-06-30` from an ordinary range selection, and the derivation quietly
 * loosened it from "the whole of June must be free" to "any one day in June is free" while the
 * header kept saying "1. Juni - 30. Juni". Encoding the mode means a link, a bookmark or a
 * hand-edited URL says which question it is asking instead of being guessed at, and every
 * `?dates=` link already in the wild keeps the meaning it had before the gate existed.
 *
 * The param is a hint, not an authority: `dateMode=month` over a span that is not a whole
 * calendar month is ignored and read as `range`, because the data is the thing being queried.
 *
 * `range` mode exists for the same reason it always did: collapsing an arbitrary span to its
 * start date would silently narrow a seven-day search to one day.
 *
 * @param {string|null|undefined} datesParam e.g. '2027-06-14,2027-06-14'
 * @param {string|null|undefined} dateModeParam 'month', or anything else for the derived modes
 * @returns {DateContext|null} null when absent, unparseable, or inverted
 */
export const parseDateContext = (datesParam, dateModeParam) => {
  if (!datesParam || typeof datesParam !== 'string') {
    return null;
  }

  const [from, to] = datesParam.split(',');
  if (!DATE_RE.test(from || '') || !DATE_RE.test(to || '')) {
    return null;
  }

  const fromDate = parseDateFromISO8601(from, MARKETPLACE_TIME_ZONE);
  const toDate = parseDateFromISO8601(to, MARKETPLACE_TIME_ZONE);
  if (!fromDate || !toDate || isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
    return null;
  }

  // moment is lenient about out-of-range components (2027-13-01 rolls into 2028-01-01), so
  // confirm the parsed date still stringifies to what we were given.
  if (
    stringifyDateToISO8601(fromDate, MARKETPLACE_TIME_ZONE) !== from ||
    stringifyDateToISO8601(toDate, MARKETPLACE_TIME_ZONE) !== to
  ) {
    return null;
  }

  // Zero-padded ISO dates order correctly as plain strings, and both are known well-formed by
  // now. An inverted range cannot mean anything, so it is no more valid than a malformed one.
  if (from > to) {
    return null;
  }

  if (from === to) {
    return { mode: 'day', date: from };
  }

  if (dateModeParam !== DATE_MODE_MONTH) {
    return { mode: 'range', start: from, end: to };
  }

  const monthStart = getStartOf(fromDate, 'month', MARKETPLACE_TIME_ZONE);
  const nextMonthStart = addTime(monthStart, 1, 'months', MARKETPLACE_TIME_ZONE);
  const monthEnd = addTime(nextMonthStart, -1, 'days', MARKETPLACE_TIME_ZONE);

  const isWholeMonth =
    stringifyDateToISO8601(monthStart, MARKETPLACE_TIME_ZONE) === from &&
    stringifyDateToISO8601(monthEnd, MARKETPLACE_TIME_ZONE) === to;

  // The mode param is a hint about a span, not a licence to reinterpret one. A `dateMode=month`
  // stuck onto a span that is not a whole calendar month is simply wrong, so the span wins.
  return isWholeMonth
    ? { mode: 'month', month: from.slice(0, 7) }
    : { mode: 'range', start: from, end: to };
};

/**
 * Write a DateContext back into the query params that carry it.
 *
 * Both parts are always returned so that a caller can write the pair in one go: `dateMode` is
 * null for every mode but `month`, and writing null is how this codebase's filters clear a query
 * param (see stringify in util/urlHelpers.js). A caller that emitted only the truthy half would
 * leave a stale `dateMode=month` behind when the user moved from a month to a day.
 *
 * @param {DateContext|null} ctx
 * @returns {{ dates: string|null, dateMode: string|null }}
 *   e.g. { dates: '2027-06-01,2027-06-30', dateMode: 'month' }
 */
export const serializeDateContext = ctx => {
  if (!ctx) {
    return { dates: null, [DATE_MODE_QUERY_PARAM]: null };
  }

  if (ctx.mode === 'day') {
    return { dates: `${ctx.date},${ctx.date}`, [DATE_MODE_QUERY_PARAM]: null };
  }

  if (ctx.mode === 'range') {
    return { dates: `${ctx.start},${ctx.end}`, [DATE_MODE_QUERY_PARAM]: null };
  }

  const monthStart = parseDateFromISO8601(`${ctx.month}-01`, MARKETPLACE_TIME_ZONE);
  const nextMonthStart = addTime(monthStart, 1, 'months', MARKETPLACE_TIME_ZONE);
  const monthEnd = addTime(nextMonthStart, -1, 'days', MARKETPLACE_TIME_ZONE);

  return {
    dates: `${ctx.month}-01,${stringifyDateToISO8601(monthEnd, MARKETPLACE_TIME_ZONE)}`,
    [DATE_MODE_QUERY_PARAM]: DATE_MODE_MONTH,
  };
};

const MINUTES_IN_A_DAY = 1440;

/**
 * Month mode's "at least one whole free day" minimum, in minutes.
 *
 * It cannot be a flat 1440. Switzerland springs forward on the last Sunday in March, and that
 * calendar day is only 1380 minutes long. A listing whose only free day in March 2027 is the
 * 28th offers a 1380-minute block, so a `minDuration: 1440` excludes it from a March search —
 * the one day it is free is shorter than the minimum being demanded.
 *
 * Subtracting an hour is the same allowance the stock Sharetribe code made for exactly this
 * transition. The cost is symmetrical and small: in autumn a 25-hour day is over-served, and a
 * listing free for 23 of a day's 24 hours also matches. Both are the right way to be wrong for
 * a filter that means "there is a day free in this month".
 */
export const MIN_DURATION_ONE_DAY = MINUTES_IN_A_DAY - 60;

/**
 * Turn a DateContext into Marketplace API availability params.
 *
 * Day mode asks for the whole day with 'time-full' — no minDuration is needed, because
 * 'time-full' already requires the entire range to be free. Range mode asks the same question
 * over the whole span, which is the semantics the stock check-in/check-out picker has always
 * had. Month mode is the one that relaxes: at least one free day somewhere inside the month,
 * which is what a couple shopping a season means.
 *
 * A context that has already passed produces no params at all rather than a filter nobody can
 * satisfy — this covers a tab left open overnight. "Passed" means the whole span is behind us:
 * a range that merely started in the past still has bookable days left in it.
 *
 * Hourly listings get a looser question, see datesQueryParams below.
 */
const dayQueryParams = (ctx, now) => {
  if (!ctx) {
    return {};
  }

  const today = getStartOf(now, 'day', MARKETPLACE_TIME_ZONE);

  if (ctx.mode === 'day') {
    const start = parseDateFromISO8601(ctx.date, MARKETPLACE_TIME_ZONE);
    if (start.getTime() < today.getTime()) {
      return {};
    }
    return {
      start,
      end: addTime(start, 1, 'days', MARKETPLACE_TIME_ZONE),
      availability: 'time-full',
    };
  }

  if (ctx.mode === 'range') {
    const start = parseDateFromISO8601(ctx.start, MARKETPLACE_TIME_ZONE);
    // The API's `end` is exclusive, so a range through the 20th ends at the 21st's midnight.
    const end = addTime(
      parseDateFromISO8601(ctx.end, MARKETPLACE_TIME_ZONE),
      1,
      'days',
      MARKETPLACE_TIME_ZONE
    );
    if (end.getTime() <= today.getTime()) {
      return {};
    }
    return { start, end, availability: 'time-full' };
  }

  const start = parseDateFromISO8601(`${ctx.month}-01`, MARKETPLACE_TIME_ZONE);
  const end = addTime(start, 1, 'months', MARKETPLACE_TIME_ZONE);
  if (end.getTime() <= today.getTime()) {
    return {};
  }

  return {
    start,
    end,
    availability: 'time-partial',
    minDuration: MIN_DURATION_ONE_DAY,
  };
};

const MIN_DURATION_ONE_HOUR = 60;

/**
 * Availability params for one unit type over a DateContext's window.
 *
 * An hourly calendar usually covers working hours only, so 'time-full' over a whole day would
 * hide every hourly vendor. They count as free when at least one hour in the window is.
 *
 * @param {DateContext|null} ctx
 * @param {Date} [now] injectable for tests; defaults to the current time
 * @param {string} [unitType] the listing's unit type, e.g. 'day' or 'hour'
 * @returns {Object} params to spread into sdk.listings.query, or {} for no date filtering
 */
export const datesQueryParams = (ctx, now = new Date(), unitType = null) => {
  const params = dayQueryParams(ctx, now);
  if (unitType !== HOUR || !params.start) {
    return params;
  }
  return {
    start: params.start,
    end: params.end,
    availability: 'time-partial',
    minDuration: MIN_DURATION_ONE_HOUR,
  };
};

/**
 * Human label for a DateContext, formatted in the marketplace's zone.
 *
 * A wedding is one day, so a day context reads '14. Juni 2027' and a month 'Juni 2027' — never
 * '14. Juni - 14. Juni'. The picker cannot emit a 'range', but a link, a bookmark or a
 * hand-edited URL can, so a span is labelled as the span it is rather than passed off as a
 * month. The Dates being formatted are Zurich midnights, hence the explicit timeZone: a browser
 * west of Zurich would otherwise render a month start as the previous month.
 *
 * Note: BookingDateRangeFilter.js grew this same formatting inline before this helper existed.
 * It can drop its copy for this whenever that file is next touched — left alone for now so this
 * change does not collide with the flexible-date work in flight there.
 *
 * @param {DateContext|null} ctx
 * @param {Object} intl react-intl instance
 * @returns {string|null} null when there is no context to label
 */
export const formatDateContext = (ctx, intl) => {
  if (!ctx) {
    return null;
  }

  const formatDay = isoDate =>
    intl.formatDate(parseDateFromISO8601(isoDate, MARKETPLACE_TIME_ZONE), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: MARKETPLACE_TIME_ZONE,
    });

  if (ctx.mode === 'day') {
    return formatDay(ctx.date);
  }

  if (ctx.mode === 'range') {
    return `${formatDay(ctx.start)} - ${formatDay(ctx.end)}`;
  }

  return intl.formatDate(parseDateFromISO8601(`${ctx.month}-01`, MARKETPLACE_TIME_ZONE), {
    month: 'long',
    year: 'numeric',
    timeZone: MARKETPLACE_TIME_ZONE,
  });
};
